import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { MbsPopupType } from '../utils';
import Alert from './alert.model';

export const alertDefaultId = 'default-alert';

@Injectable({ providedIn: 'root' })
export class AlertService {
  #close$: Subject<Alert> = new Subject();
  #alert$: Subject<Alert> = new Subject();
  alerts$ = new BehaviorSubject<Array<Alert>>([]);

  /**
   * Emit event for resolve all listeners.
   * @param {Alert} alert
   * @return {void}
   */
  close(alert: Alert): void {
    this.alerts$.next(this.alerts$.value.filter((x) => x !== alert));
    this.#close$.next(alert);
  }

  /**
   * enable subscribing to Alert observable
   * @param {string} id
   * @return {Observable<Alert>}
   */
  onAlert(id: string = alertDefaultId): Observable<Alert> {
    return this.#alert$.pipe(filter((x) => x?.id === id));
  }

  /**
   * alias for calling Alert by type: `MbsPopupType.success`
   * @param {string} msg
   * @param {Partial<Alert>} options
   * @return {Observable<Alert>}
   */
  success(msg?: string, options?: Partial<Alert>): Observable<Alert> {
    const content: string = msg ? msg : 'Success';

    return this.alert(new Alert({ ...options, type: MbsPopupType.success, content }));
  }

  /**
   * alias for calling Alert by type: `MbsPopupType.danger`
   * @param {string} msg
   * @param {Partial<Alert>} options
   * @return {Observable<Alert>}
   */
  error(msg?: string, options?: Partial<Alert>): Observable<Alert> {
    const content: string = msg ? msg : 'Sorry, something went wrong';

    return this.alert(new Alert({ ...options, type: MbsPopupType.danger, content }));
  }

  /**
   * alias for calling Alert by type: `MbsPopupType.info`
   * @param {string} content
   * @param {Partial<Alert>} options
   * @return {Observable<Alert>}
   */
  info(content: string, options?: Partial<Alert>): Observable<Alert> {
    return this.alert(new Alert({ ...options, type: MbsPopupType.info, content }));
  }

  /**
   * alias for calling Alert by type: `MbsPopupType.warning`
   * @param {string} content
   * @param {Partial<Alert>} options
   * @return {Observable<Alert>}
   */
  warn(content: string, options?: Partial<Alert>): Observable<Alert> {
    return this.alert(new Alert({ ...options, type: MbsPopupType.warning, content }));
  }

  /**
   * Main alert method
   * @param {Alert} alert
   * @return {Observable<Alert>}
   */
  alert(alert: Alert): Observable<Alert> {
    alert.id = alert.id || alertDefaultId;
    this.#alert$.next(alert);
    this.alerts$.next([...this.alerts$.value, alert]);
    return this.#close$.pipe(first((a) => a === alert));
  }

  /**
   * Remove Alert by id
   * @param {string} id
   * @return {void}
   */
  clearById(id: string = alertDefaultId): void {
    this.#alert$.next(new Alert({ id }));
    this.alerts$.next(this.alerts$.value.filter((alert) => alert.id !== id));
  }

  clear() {
    this.alerts$.next([]);
  }
}
